import {
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { Gladior_Management_Annual_Review } from "../types/types";
import { ArrowForward as ArrowForwardIcon, ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import useAfasData from "../hooks/useAfasData";
import EmployeeIcon from "../icons/EmployeeIcon";
import EmployerIcon from "../icons/EmployerIcon";
import { LanguageContext } from "../context/LanguageContext";

interface Strings {
  interviews: {
    actionemployee: string;
    actionmanager: string;
    unknown: string;
  };
}

function getNameOfResponsible(
  action_code: Gladior_Management_Annual_Review["action_code"],
  strings: Strings
): string {
  switch (action_code) {
    case "000":
      return strings.interviews.actionemployee;
    case "001":
      return strings.interviews.actionmanager;
    default:
      return strings.interviews.unknown;
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getIconColorByType = (type: Gladior_Management_Annual_Review["action_code"], theme: any) => {
  switch (type) {
    case "000": // Medewerker
      return theme.palette.lightblue.main;
    case "001": // Manager
      return theme.palette.primary.main;
    default: // Onbekend
      return theme.palette.secondary.main;
  }
};

function getIconByType(action_code: Gladior_Management_Annual_Review["action_code"]) {
  switch (action_code) {
    case "000":
      return <EmployeeIcon sx={{ fontSize: "1rem", width: "16px", height: "16px" }} />;
    case "001":
      return <EmployerIcon sx={{ fontSize: "1rem", width: "16px", height: "16px" }} />;
    default:
      return <EmployerIcon sx={{ fontSize: "1rem", width: "16px", height: "16px" }} />;
  }
}

const UpcomingConversationsComponent = () => {
  const { strings } = useContext(LanguageContext);
  const rowsPerPage = 6;
  const [page, setPage] = useState(0);
  const [ongoingReviews, setOngoingReviews] = useState<Gladior_Management_Annual_Review[]>([]);
  const { data: reviewsData, isLoading: reviewsDataLoading } = useAfasData("/team/reviews");
  useEffect(() => {
    if (!reviewsDataLoading && reviewsData) {
      setOngoingReviews(reviewsData);
    }
  }, [reviewsData]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Calculate pagination info
  const totalPages =
    Math.ceil(ongoingReviews.length / rowsPerPage) > 0 ? Math.ceil(ongoingReviews.length / rowsPerPage) : 1;
  const currentPageStart = page * rowsPerPage;
  const currentPageEnd = Math.min((page + 1) * rowsPerPage, ongoingReviews.length);
  const currentOngoingReviews = ongoingReviews.slice(currentPageStart, currentPageEnd);

  return (
    <>
      <Stack direction={"column"} sx={{ height: "100%" }}>
        <Typography mb={3} variant="h6">
          {strings.interviews.interviews}
        </Typography>
        <List disablePadding sx={{ mb: 1, flex: "1 1 auto" }}>
          {ongoingReviews.length === 0 && !reviewsDataLoading && (
            <Typography variant="body1" color="inherit">
              {strings.interviews.nointerviews}
            </Typography>
          )}
          {reviewsDataLoading && <CircularProgress />}
          {currentOngoingReviews.map((review) => (
            <ListItem key={review.id} sx={{ pt: "0", pb: "1rem", px: 0 }}>
              <ListItemAvatar sx={{ minWidth: "unset" }}>
                <Tooltip
                  arrow
                  placement="top"
                  title={getNameOfResponsible(review.action_code, strings as Strings)}
                >
                  <Avatar
                    sx={(theme) => ({
                      width: "30px",
                      height: "30px",
                      bgcolor: getIconColorByType(review.action_code, theme),
                      mr: 2,
                    })}
                  >
                    {getIconByType(review.action_code)}
                  </Avatar>
                </Tooltip>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Tooltip arrow placement="top" title={review.employee_name}>
                    <Typography
                      sx={{
                        display: "-webkit-box",
                        "-webkit-line-clamp": "1",
                        lineClamp: "1",
                        "-webkit-box-orient": "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      variant="body1"
                      color="initial"
                      component={"span"}
                    >
                      {review.employee_name}
                    </Typography>
                  </Tooltip>
                }
              />
            </ListItem>
          ))}
        </List>
        <Stack
          direction="row"
          sx={{
            justifyContent: "flex-end",
            alignItems: "center",
            mt: 1,
          }}
        >
          {totalPages !== 1 && (
            <Box display="flex" alignItems="center">
              <IconButton onClick={() => handleChangePage(null, page - 1)} disabled={page === 0} size="small">
                <ArrowBackIcon />
              </IconButton>
              <Typography component="span" sx={{ mx: 1 }}>
                {page + 1}/
                <Typography component={"span"} fontWeight={"bold"}>
                  {totalPages}
                </Typography>
              </Typography>
              <IconButton
                onClick={() => handleChangePage(null, page + 1)}
                disabled={page >= totalPages - 1}
                size="small"
              >
                <ArrowForwardIcon />
              </IconButton>
            </Box>
          )}
        </Stack>
      </Stack>
    </>
  );
};

export default UpcomingConversationsComponent;
